import * as React from "react";

import { cn } from "@/lib/utils";
import { type ButtonProps, Button } from "@/shared/components/ui/button";
import { CircularProgress } from "@/shared/components/ui/circular-progress";

export interface AsyncButtonProps extends ButtonProps {
  loadingText?: string;
  loading?: boolean;
  minLoadTime?: number;
}

const AsyncButton = React.forwardRef<HTMLButtonElement, AsyncButtonProps>(
  ({ loading = false, loadingText = "Loading...", disabled, minLoadTime = 1500, children, ...props }, ref) => {
    const [showLoading, setShowLoading] = React.useState(loading);
    const startTimeRef = React.useRef(0);

    React.useEffect(() => {
      let timeoutId: ReturnType<typeof setTimeout>;
      // if loading becomes true
      if (loading) {
        setShowLoading(true);
        // start the timer
        startTimeRef.current = Date.now();
        timeoutId = setTimeout(() => {
          // at the end of the timer, set showLoading to the current state of loading
          setShowLoading(loading);
        });
      } else if (Date.now() - startTimeRef.current < minLoadTime) {
        // if loading becomes false before the timer ends, set a timeout to end the load state after the minimum load time
        timeoutId = setTimeout(
          () => {
            setShowLoading(false);
          },
          minLoadTime - (Date.now() - startTimeRef.current),
        );
      } else {
        // else loading becomes false after the timer ends, set showLoading to false
        setShowLoading(false);
      }

      return () => {
        clearTimeout(timeoutId);
      };
    }, [loading, minLoadTime]);

    return (
      <Button data-slot="async-button" disabled={showLoading || disabled} ref={ref} {...props}>
        {showLoading ? (
          <>
            <CircularProgress className={cn("text-inherit", loadingText && "mr-1")} />
            {loadingText}
          </>
        ) : (
          children
        )}
      </Button>
    );
  },
);

AsyncButton.displayName = "AsyncButton";

export { AsyncButton };
